import React, { useEffect, useState } from 'react';
import { PaginationProps } from '../../interfaces/types';
import '../../styles/Pagination.css';

const Pagination: React.FC<PaginationProps> = ({
  page,
  rowsPerPage,
  count,
  rowsPerPageOptions,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [showRowsPerPageMenu, setShowRowsPerPageMenu] = useState(false);
  const [showPageMenu, setShowPageMenu] = useState(false);
  const rowsPerPageDefaultOptions = [10, 25, 50, 100];

  const getPageData = () => {
    return {
      totalPages: Math.ceil(count / rowsPerPage),
      startRecord: count === 0 ? 0 : page * rowsPerPage + 1,
      endRecord: Math.min((page + 1) * rowsPerPage, count),
    };
  };

  const [pageData, setPageData] = useState(getPageData());

  useEffect(() => {
    setPageData(getPageData());
  }, [count, page, rowsPerPage]);

  const handleFirst = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLButtonElement)?.blur?.();
    onPageChange(0);
  };
  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLButtonElement)?.blur?.();
    onPageChange(Math.max(0, page - 1));
  };
  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLButtonElement)?.blur?.();
    onPageChange(Math.min(pageData.totalPages - 1, page + 1));
  };
  const handleLast = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLButtonElement)?.blur?.();
    onPageChange(Math.max(0, pageData.totalPages - 1));
  };

  const handlePageChange = (
    e: React.MouseEvent | undefined,
    newPage: number,
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLButtonElement)?.blur?.();
    }
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    e: React.MouseEvent | undefined,
    newValue: number,
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLButtonElement)?.blur?.();
    }
    if (onRowsPerPageChange) {
      onPageChange(0);
      onRowsPerPageChange(newValue);
    }
    setShowRowsPerPageMenu(false);
  };

  return (
    <div className="pagination-root">
      <div className="pagination-info">
        <span className="pagination-label">Records</span>
        <span className="pagination-value">
          {pageData.startRecord}-{pageData.endRecord} of {count}
        </span>
        <div className="pagination-dot" />
        <span className="pagination-label">Per Page</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            type="button"
            className="pagination-rows-button"
            onClick={() => setShowRowsPerPageMenu(!showRowsPerPageMenu)}
            aria-label="Select rows per page"
            aria-expanded={showRowsPerPageMenu}
          >
            <span>{rowsPerPage}</span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M5 7.5L1 3.5L1.5 3L5 6.5L8.5 3L9 3.5L5 7.5Z"
                fill="currentColor"
              />
            </svg>
          </button>
          {showRowsPerPageMenu && (
            <div className="pagination-rows-menu">
              {(rowsPerPageOptions?.length
                ? rowsPerPageOptions
                : rowsPerPageDefaultOptions
              ).map(option => (
                <button
                  type="button"
                  key={option}
                  className={`pagination-rows-menu-item ${
                    option === rowsPerPage ? 'is-active' : ''
                  }`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRowsPerPageChange(e, option);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-icon-btn"
          disabled={page === 0}
          onClick={handleFirst}
          title="First page"
          aria-label="First page"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M5.25 13.5C5.0375 13.5 4.85938 13.4281 4.71563 13.2844C4.57188 13.1406 4.5 12.9625 4.5 12.75V5.25C4.5 5.0375 4.57188 4.85938 4.71563 4.71563C4.85938 4.57188 5.0375 4.5 5.25 4.5C5.4625 4.5 5.64062 4.57188 5.78438 4.71563C5.92813 4.85938 6 5.0375 6 5.25V12.75C6 12.9625 5.92813 13.1406 5.78438 13.2844C5.64062 13.4281 5.4625 13.5 5.25 13.5ZM10.35 9L13.275 11.925C13.4125 12.0625 13.4813 12.2375 13.4813 12.45C13.4813 12.6625 13.4125 12.8375 13.275 12.975C13.1375 13.1125 12.9625 13.1813 12.75 13.1813C12.5375 13.1813 12.3625 13.1125 12.225 12.975L8.775 9.525C8.7 9.45 8.64688 9.36875 8.61563 9.28125C8.58438 9.19375 8.56875 9.1 8.56875 9C8.56875 8.9 8.58438 8.80625 8.61563 8.71875C8.64688 8.63125 8.7 8.55 8.775 8.475L12.225 5.025C12.3625 4.8875 12.5375 4.81875 12.75 4.81875C12.9625 4.81875 13.1375 4.8875 13.275 5.025C13.4125 5.1625 13.4813 5.3375 13.4813 5.55C13.4813 5.7625 13.4125 5.9375 13.275 6.075L10.35 9Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <button
          type="button"
          className="pagination-icon-btn"
          disabled={page === 0}
          onClick={handlePrev}
          title="Previous page"
          aria-label="Previous page"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M8.1001 9.0001L11.0251 11.9251C11.1626 12.0626 11.2313 12.2376 11.2313 12.4501C11.2313 12.6626 11.1626 12.8376 11.0251 12.9751C10.8876 13.1126 10.7126 13.1813 10.5001 13.1813C10.2876 13.1813 10.1126 13.1126 9.9751 12.9751L6.5251 9.5251C6.4501 9.4501 6.39697 9.36885 6.36572 9.28135C6.33447 9.19385 6.31885 9.1001 6.31885 9.0001C6.31885 8.9001 6.33447 8.80635 6.36572 8.71885C6.39697 8.63135 6.4501 8.5501 6.5251 8.4751L9.9751 5.0251C10.1126 4.8876 10.2876 4.81885 10.5001 4.81885C10.7126 4.81885 10.8876 4.8876 11.0251 5.0251C11.1626 5.1626 11.2313 5.3376 11.2313 5.5501C11.2313 5.7626 11.1626 5.9376 11.0251 6.0751L8.1001 9.0001Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <div className="pagination-page-select">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              className="pagination-rows-button"
              onClick={() => setShowPageMenu(!showPageMenu)}
              aria-label="Select page"
              aria-expanded={showPageMenu}
            >
              <span>{page + 1}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M5 7.5L1 3.5L1.5 3L5 6.5L8.5 3L9 3.5L5 7.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>
            {showPageMenu && (
              <div className="pagination-rows-menu">
                {Array.from({ length: pageData.totalPages }, (_, i) => (
                  <button
                    type="button"
                    key={i + 1}
                    className={`pagination-rows-menu-item ${
                      i + 1 === page + 1 ? 'is-active' : ''
                    }`}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePageChange(e, i);
                      setShowPageMenu(false);
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="pagination-page-text">
            of {pageData.totalPages} pages
          </span>
        </div>

        <button
          type="button"
          className="pagination-icon-btn"
          disabled={page >= pageData.totalPages - 1}
          onClick={handleNext}
          title="Next page"
          aria-label="Next page"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9.4501 9.0001L6.5251 6.0751C6.3876 5.9376 6.31885 5.7626 6.31885 5.5501C6.31885 5.3376 6.3876 5.1626 6.5251 5.0251C6.6626 4.8876 6.8376 4.81885 7.0501 4.81885C7.2626 4.81885 7.4376 4.8876 7.5751 5.0251L11.0251 8.4751C11.1001 8.5501 11.1532 8.63135 11.1845 8.71885C11.2157 8.80635 11.2313 8.9001 11.2313 9.0001C11.2313 9.1001 11.2157 9.19385 11.1845 9.28135C11.1532 9.36885 11.1001 9.4501 11.0251 9.5251L7.5751 12.9751C7.4376 13.1126 7.2626 13.1813 7.0501 13.1813C6.8376 13.1813 6.6626 13.1126 6.5251 12.9751C6.3876 12.8376 6.31885 12.6626 6.31885 12.4501C6.31885 12.2376 6.3876 12.0626 6.5251 11.9251L9.4501 9.0001Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <button
          type="button"
          className="pagination-icon-btn"
          disabled={page >= pageData.totalPages - 1}
          onClick={handleLast}
          title="Last page"
          aria-label="Last page"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M7.6498 9L4.7248 6.075C4.5873 5.9375 4.51855 5.7625 4.51855 5.55C4.51855 5.3375 4.5873 5.1625 4.7248 5.025C4.8623 4.8875 5.0373 4.81875 5.2498 4.81875C5.4623 4.81875 5.6373 4.8875 5.7748 5.025L9.2248 8.475C9.2998 8.55 9.35293 8.63125 9.38418 8.71875C9.41543 8.80625 9.43106 8.9 9.43106 9C9.43106 9.1 9.41543 9.19375 9.38418 9.28125C9.35293 9.36875 9.2998 9.45 9.2248 9.525L5.7748 12.975C5.6373 13.1125 5.4623 13.1813 5.2498 13.1813C5.0373 13.1813 4.8623 13.1125 4.7248 12.975C4.5873 12.8375 4.51855 12.6625 4.51855 12.45C4.51855 12.2375 4.5873 12.0625 4.7248 11.925L7.6498 9ZM12.7498 4.5C12.9623 4.5 13.1404 4.57188 13.2842 4.71563C13.4279 4.85938 13.4998 5.0375 13.4998 5.25V12.75C13.4998 12.9625 13.4279 13.1406 13.2842 13.2844C13.1404 13.4281 12.9623 13.5 12.7498 13.5C12.5373 13.5 12.3592 13.4281 12.2154 13.2844C12.0717 13.1406 11.9998 12.9625 11.9998 12.75V5.25C11.9998 5.0375 12.0717 4.85938 12.2154 4.71563C12.3592 4.57188 12.5373 4.5 12.7498 4.5Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
